/**
 * SlashCommandList
 *
 * Phase-aware dropdown rendered inside the command palette when the user
 * types a "/" prefix.
 *
 * Phases:
 *  'verb' — show all top-level verb cards (/ai, /add)
 *  'sub'  — show matching sub-commands for the active verb
 *  'arg'  — show a single selected item + "press Enter" hint
 *
 * The component is purely presentational. State lives in CommandPalette.
 */

import { useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import type { ParsedSlash } from '@/commands/slashParser';
import type { SlashVerb, SlashSubcommand } from '@/commands/slashCommands';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SlashCommandListProps {
  parsed: ParsedSlash;
  verbs: SlashVerb[];
  /** Currently highlighted item index */
  activeIndex: number;
  onActiveIndexChange: (index: number) => void;
  /** Called when an item is selected (clicked or Enter) */
  onSelect: (verb: SlashVerb, sub?: SlashSubcommand) => void;
}

// ─── Sub-item row ─────────────────────────────────────────────────────────────

function SubItem({
  verb,
  sub,
  isActive,
  arg,
  onSelect,
  onHover,
}: {
  verb: SlashVerb;
  sub: SlashSubcommand;
  isActive: boolean;
  arg?: string | null;
  onSelect: () => void;
  onHover: () => void;
}) {
  const ref = useRef<HTMLButtonElement>(null);

  // Scroll into view when active
  useEffect(() => {
    if (isActive) {
      ref.current?.scrollIntoView({ block: 'nearest' });
    }
  }, [isActive]);

  const Icon = sub.icon ?? verb.icon;
  const hasArgValue = arg !== null && arg !== undefined && arg.trim().length > 0;

  return (
    <button
      ref={ref}
      type="button"
      onMouseEnter={onHover}
      onClick={onSelect}
      className={cn(
        'flex w-full items-start gap-3 rounded-md px-3 py-2.5 text-left transition-colors',
        isActive
          ? 'bg-accent text-accent-foreground'
          : 'text-foreground hover:bg-accent/60',
      )}
    >
      {/* Icon */}
      <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center text-muted-foreground">
        {Icon ? <Icon className="h-3.5 w-3.5" /> : null}
      </span>

      {/* Label + description */}
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex items-baseline gap-1.5">
          <span className="font-mono text-sm font-medium">
            /{verb.verb} {sub.name}
          </span>
          {sub.argLabel && (
            <span className="font-mono text-xs text-muted-foreground">
              {sub.argLabel}
            </span>
          )}
          {/* Show the user's typed arg */}
          {hasArgValue && (
            <span className="truncate font-mono text-xs text-primary/80">
              {arg}
            </span>
          )}
        </span>
        <span className="text-xs text-muted-foreground">{sub.description}</span>
      </span>

      {/* Enter hint when arg is filled or sub needs no arg */}
      {isActive && (!sub.hasArg || hasArgValue) && (
        <span className="ml-auto shrink-0 self-center rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
          ↵
        </span>
      )}
    </button>
  );
}

// ─── Verb card (shown in 'verb' phase) ────────────────────────────────────────

function VerbCard({
  verb,
  isActive,
  onSelect,
  onHover,
}: {
  verb: SlashVerb;
  isActive: boolean;
  onSelect: () => void;
  onHover: () => void;
}) {
  const ref = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isActive) {
      ref.current?.scrollIntoView({ block: 'nearest' });
    }
  }, [isActive]);

  const Icon = verb.icon;
  // Show first few sub names as a preview
  const subPreview = verb.subcommands
    .slice(0, 4)
    .map((s) => s.name)
    .join(', ');

  return (
    <button
      ref={ref}
      type="button"
      onMouseEnter={onHover}
      onClick={onSelect}
      className={cn(
        'flex w-full items-start gap-3 rounded-md px-3 py-2.5 text-left transition-colors',
        isActive
          ? 'bg-accent text-accent-foreground'
          : 'text-foreground hover:bg-accent/60',
      )}
    >
      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded bg-primary/10 text-primary">
        {Icon ? <Icon className="h-3 w-3" /> : null}
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex items-baseline gap-2">
          <span className="font-mono text-sm font-semibold">/{verb.verb}</span>
          <span className="text-xs text-muted-foreground">{verb.description}</span>
        </span>
        {subPreview && (
          <span className="font-mono text-[11px] text-muted-foreground/70">
            {subPreview}
            {verb.subcommands.length > 4 ? ` +${verb.subcommands.length - 4} more` : ''}
          </span>
        )}
      </span>
      <span className="ml-auto shrink-0 self-center rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
        →
      </span>
    </button>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────

export function SlashCommandList({
  parsed,
  verbs,
  activeIndex,
  onActiveIndexChange,
  onSelect,
}: SlashCommandListProps) {
  const { phase, verb: verbStr, sub: subStr, arg } = parsed;

  // ── Phase: verb ──────────────────────────────────────────────────────────────
  if (phase === 'verb') {
    const filteredVerbs =
      verbStr
        ? verbs.filter((v) => v.verb.startsWith(verbStr))
        : verbs;

    if (filteredVerbs.length === 0) {
      return (
        <div className="flex flex-col gap-1 p-3">
          <p className="text-center text-sm text-muted-foreground">
            No matching commands for <code className="font-mono">/{verbStr}</code>
          </p>
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-1 p-1">
        <p className="px-2 py-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          Slash commands
        </p>
        {filteredVerbs.map((v, i) => (
          <VerbCard
            key={v.verb}
            verb={v}
            isActive={i === activeIndex}
            onSelect={() => onSelect(v)}
            onHover={() => onActiveIndexChange(i)}
          />
        ))}
      </div>
    );
  }

  // ── Phase: sub or arg ────────────────────────────────────────────────────────
  const activeVerb = verbs.find((v) => v.verb === verbStr);

  if (!activeVerb) {
    return (
      <div className="flex flex-col gap-1 p-3">
        <p className="text-center text-sm text-muted-foreground">
          Unknown command <code className="font-mono">/{verbStr}</code>
        </p>
      </div>
    );
  }

  // Filter sub-commands by partial match (phase='sub') or show all (phase='arg')
  let filteredSubs: SlashSubcommand[];
  if (phase === 'sub') {
    filteredSubs = subStr
      ? activeVerb.subcommands.filter((s) => s.name.startsWith(subStr))
      : activeVerb.subcommands;
  } else {
    // phase === 'arg': show only the exact matched sub (if any)
    filteredSubs = subStr
      ? activeVerb.subcommands.filter((s) => s.name === subStr)
      : activeVerb.subcommands;
  }

  if (filteredSubs.length === 0) {
    return (
      <div className="flex flex-col gap-1 p-3">
        <p className="text-center text-sm text-muted-foreground">
          No sub-commands matching{' '}
          <code className="font-mono">/{verbStr} {subStr}</code>
        </p>
      </div>
    );
  }

  const groupLabel =
    phase === 'arg'
      ? 'Press Enter to execute'
      : `/${activeVerb.verb} — ${activeVerb.description}`;

  return (
    <div className="flex flex-col gap-1 p-1">
      <p className="px-2 py-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        {groupLabel}
      </p>
      {filteredSubs.map((sub, i) => (
        <SubItem
          key={sub.name}
          verb={activeVerb}
          sub={sub}
          isActive={i === activeIndex}
          arg={phase === 'arg' ? arg : undefined}
          onSelect={() => onSelect(activeVerb, sub)}
          onHover={() => onActiveIndexChange(i)}
        />
      ))}
    </div>
  );
}

// ─── Helper: compute total item count for arrow navigation ────────────────────

export function getSlashItemCount(parsed: ParsedSlash, verbs: SlashVerb[]): number {
  const { phase, verb: verbStr, sub: subStr } = parsed;

  if (phase === 'verb') {
    return verbStr
      ? verbs.filter((v) => v.verb.startsWith(verbStr)).length
      : verbs.length;
  }

  const activeVerb = verbs.find((v) => v.verb === verbStr);
  if (!activeVerb) return 0;

  if (phase === 'sub') {
    return subStr
      ? activeVerb.subcommands.filter((s) => s.name.startsWith(subStr)).length
      : activeVerb.subcommands.length;
  }

  // phase === 'arg'
  return subStr
    ? activeVerb.subcommands.filter((s) => s.name === subStr).length
    : activeVerb.subcommands.length;
}

// ─── Helper: get the active item for Enter key handling ───────────────────────

export function getActiveSlashItem(
  parsed: ParsedSlash,
  verbs: SlashVerb[],
  activeIndex: number,
): { verb: SlashVerb; sub?: SlashSubcommand } | null {
  const { phase, verb: verbStr, sub: subStr } = parsed;

  if (phase === 'verb') {
    const list = verbStr
      ? verbs.filter((v) => v.verb.startsWith(verbStr))
      : verbs;
    const v = list[activeIndex];
    return v ? { verb: v } : null;
  }

  const activeVerb = verbs.find((v) => v.verb === verbStr);
  if (!activeVerb) return null;

  let subs: SlashSubcommand[];
  if (phase === 'sub') {
    subs = subStr
      ? activeVerb.subcommands.filter((s) => s.name.startsWith(subStr))
      : activeVerb.subcommands;
  } else {
    subs = subStr
      ? activeVerb.subcommands.filter((s) => s.name === subStr)
      : activeVerb.subcommands;
  }

  const s = subs[activeIndex];
  return s ? { verb: activeVerb, sub: s } : null;
}
